import type {
  GetNextBuyflowStepRequest,
  GetNextBuyflowStepResponse,
  ProductWidget,
} from '@/lib/TypesOpenApi';
import {
  store as cart,
  removeIncludedPointProducts,
  removeItemFromCart,
  setIncludedPointProduct,
  setItemToCart,
  toggleStreamingService,
  updateStreamingService,
} from '../../(state)/CartStore';
import {
  type ArrayComparisionResults,
  compareArrays,
  compareSelectedProducts,
  filterWithRest,
  isProductsEqual,
} from '../mapState2Api/compareUserSelections';

export const syncSelectedProducts = (
  oldState: GetNextBuyflowStepRequest,
  newState: GetNextBuyflowStepResponse,
) => {
  const compared = compareSelectedProducts(
    oldState.buyflowState.userSelection?.selectedProducts || [],
    newState.buyflowState.userSelection?.selectedProducts || [],
  );

  const withoutRouters = syncRouters(compared);
  const withoutStreaming = syncStreaming(withoutRouters);
  // TODO: add update logic for the rest of sections

  // will consider everything that left as additonal items
  syncAdditionalItems(withoutStreaming);
};

/** Will sync routers and return rest of the items */
export const syncRouters = ({
  added,
  removed,
  changed,
  rest,
}: ArrayComparisionResults<ProductWidget>): ArrayComparisionResults<ProductWidget> => {
  /**
   * If internet plan include routers we set it here on leaving page to display it in cart on nest step
   * Other pointCount fields set by subscription in pointCount.ts
   */
  const {result: addedRouters, rest: restAdded} = filterWithRest(
    added,
    isRouterPoint,
  );
  // will consider new routers as included product
  addedRouters.map(setIncludedPointProduct);

  const {result: removedRouters, rest: restRemoved} = filterWithRest(
    removed,
    isRouterPoint,
  );
  const removedCount = removeIncludedPointProducts(removedRouters);
  if (removedRouters.length !== removedCount) {
    console.warn(
      `Wasn't able to remove all routers, removed count: ${removedCount}, expected to remove: ${removedRouters.length}, target routers:`,
      removedRouters,
    );
  }

  const {result: changedRouters, rest: restChanged} = filterWithRest(
    changed,
    isRouterPoint,
  );
  changedRouters.map(setIncludedPointProduct);

  return {
    added: restAdded,
    removed: restRemoved,
    changed: restChanged,
    rest,
  };
};
/** Will sync streaming and return rest of the items */
export const syncStreaming = ({
  added,
  removed,
  changed,
  rest,
}: ArrayComparisionResults<ProductWidget>): ArrayComparisionResults<ProductWidget> => {
  const {result: addedStreaming, rest: restAdded} = filterWithRest(
    added,
    isStreaming,
  );

  addedStreaming.map(p => toggleStreamingService(p, p.productCost?.units ?? 0));

  const {result: removedStreaming, rest: restRemoved} = filterWithRest(
    removed,
    isStreaming,
  );
  removedStreaming.map(p =>
    toggleStreamingService(p, p.productCost?.units ?? 0),
  );

  const {result: changedStreaming, rest: restChanged} = filterWithRest(
    changed,
    isStreaming,
  );
  changedStreaming.map(updateStreamingService);

  return {
    added: restAdded,
    removed: restRemoved,
    changed: restChanged,
    rest,
  };
};

export const isRouterPoint = (x: ProductWidget) =>
  x.productType === 'CPE_POINT_PRODUCT';

export const isStreaming = (x: ProductWidget) =>
  x.productType === 'STREAMING_PRODUCT';

export const syncAdditionalItems = ({
  added,
  removed,
  changed,
}: ArrayComparisionResults<ProductWidget>) => {
  added.forEach(setItemToCart);

  const countBeforeRemoving = cart.additionalItems.length;
  removed.forEach(removeItemFromCart);
  if (countBeforeRemoving - cart.additionalItems.length !== removed.length) {
    console.warn(
      `Failed to remove all required items from additional item cart section. Removed count: ${countBeforeRemoving - cart.additionalItems.length}, expected:`,
      removed,
    );
  }

  const {same, restFromA: restCanged} = compareArrays(
    changed,
    cart.additionalItems,
    isProductsEqual,
  );
  if (restCanged.length) {
    console.warn(
      'Not all items can be updated in additional items cart section. Will not update:',
      restCanged,
    );
  }

  // update items that possible
  same.map(({a}) => a).forEach(setItemToCart);
};
